// Default CMS content for the Revenue Calculator page + merge helper.

export const RC_DEFAULT = {
  hero: {
    overline: "Partner with Happy Retreats · Full transparency",
    headline_pre: "What could your ",
    headline_accent: "property earn",
    headline_post: " in a year?",
    subheadline:
      "We believe owners deserve the same transparency our guests get. This calculator uses our humanistic approach — counting paying guests at an average ticket size, not vague occupancy promises. Enter your numbers; nothing is stored until you choose to share.",
  },
  steps: [
    { id: "type", title: "What kind of property is it?", intro: "This helps us frame the right revenue streams for you." },
    { id: "capacity", title: "Sleeping capacity & stay pricing", intro: "How many paying guests can sleep at the property each night, and at what price per head?", bed_price: 5000, cot_price: 3500 },
    { id: "spaces", title: "Event spaces", intro: "Lawns, banquet halls, poolside decks — each with its seated capacity and a per-day venue price.", enabled: true },
    { id: "leisure", title: "Leisure & corporate stays", intro: "Your minimum guarantee is every weekend — 52 days a year. Then set a realistic target above it.", enabled: true },
    { id: "weddings", title: "Weddings", intro: "A wedding buys out the whole venue. Its cost = all rooms per night + every event space + any additional charges.", enabled: true },
    { id: "socials", title: "Private socials", intro: "Birthdays, anniversaries, poojas — sell each event space by the event.", enabled: true, food_per_head: 1500 },
  ],
  summary: {
    overline: "Your projection",
    total_label: "Total projected annual revenue",
    download_label: "Download projection (PDF)",
    discuss_label: "Discuss with our team",
    disclaimer:
      "An illustrative estimate from your inputs. Actual revenue depends on seasonality, pricing and operations — we'll build the real plan together.",
  },
  closing: {
    enabled: true,
    heading: "Like what the numbers say?",
    text: "Our partnerships team will validate this projection against real market data for your location — no obligations.",
    cta_label: "Partner with us",
  },
  pdf: {
    // Cover / header band
    cover_eyebrow: "R E V E N U E   P R O J E C T I O N",
    show_contact_on_cover: true,
    // Section titles inside the PDF
    section_property: "The property",
    section_leisure: "Stream 1 — Leisure & corporate stays",
    section_weddings: "Stream 2 — Weddings",
    section_socials: "Stream 3 — Private socials",
    total_band_label: "T O T A L   P R O J E C T E D   A N N U A L   R E V E N U E",
    // Partnership page (dedicated page 2 of the PDF)
    message_enabled: true,
    message_page_eyebrow: "The partnership",
    message_headline: "Why partner with Happy Retreats",
    message_items: [
      { title: "Home-Style Hospitality", body: "Guests are treated with warmth, care and personal attention — the experience feels less like a standard venue and more like being looked after by an extended family. That creates better guest experiences, stronger reviews and repeat business." },
      { title: "A Full-Stack Partnership", body: "Branding, marketing, bookings and on-ground operations are handled for you — you stay the owner, we run the celebration engine." },
      { title: "Part of a Larger Brand Story", body: "Property owners join a growing hospitality brand with a clear vision and deep experience in celebrations. You are not handing over a venue — you are becoming part of Karnataka's celebration circuit." },
    ],
    stats_enabled: true,
    stats: [
      { value: "2019", label: "Established in" },
      { value: "500+", label: "Celebrations hosted" },
      { value: "25+", label: "Curated venues" },
      { value: "4.8 / 5", label: "Average guest rating" },
    ],
    // Signature block
    signature_enabled: true,
    signature_name: "Happy Retreats Partnerships",
    signature_designation: "Owner Success Team",
    signature_email: "partners@happyretreats.in",
    signature_phone: "+91 —",
    // Disclaimer + footer
    disclaimer:
      "This projection is an illustrative estimate built from the numbers you entered. Actual revenue depends on seasonality, pricing strategy, marketing and operations. Happy Retreats shares this transparently so we can plan your property's potential together.",
    footer_brand: "Happy Retreats",
    footer_url_line: "happyretreats.in   ·   Partner with us",
    footer_tagline: "C E L E B R A T I O N   G E T A W A Y S",
  },
};

export const mergeRC = (c) => {
  if (!c) return RC_DEFAULT;
  const byId = Object.fromEntries(RC_DEFAULT.steps.map((s) => [s.id, s]));
  const stored = (c.steps || []).filter((s) => s && byId[s.id]).map((s) => ({ ...byId[s.id], ...s }));
  const missing = RC_DEFAULT.steps.filter((d) => !stored.find((s) => s.id === d.id));
  let steps = [...stored, ...missing];
  steps = [steps.find((s) => s.id === "type"), ...steps.filter((s) => s.id !== "type")];
  const pdfStored = c.pdf || {};
  const pdf = { ...RC_DEFAULT.pdf, ...pdfStored };
  if (!pdfStored.message_items) {
    const bullets = [pdfStored.message_bullet_1, pdfStored.message_bullet_2, pdfStored.message_bullet_3].filter((b) => (b || "").trim());
    if (bullets.length) pdf.message_items = bullets.map((b) => ({ title: "", body: b }));
  }
  return {
    hero: { ...RC_DEFAULT.hero, ...(c.hero || {}) },
    steps,
    summary: { ...RC_DEFAULT.summary, ...(c.summary || {}) },
    closing: { ...RC_DEFAULT.closing, ...(c.closing || {}) },
    pdf,
  };
};
